import React, { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Tag as TagIcon,
  Bookmark as BookmarkIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewReleasesIcon,
  FilterList as FilterListIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Timer as TimerIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import logo0 from '../assets/logo0.png';
import complaintsImg from '../assets/Complaints.png';
import { Link as RouterLink } from 'react-router-dom';

const DRAWER_WIDTH = 240;

const modules = {
  toolbar: [
    [{ 'header': [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
    [{'list': 'ordered'}, {'list': 'bullet'}],
    ['link', 'image'],
    ['clean']
  ],
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
  'list', 'bullet',
  'link', 'image'
];

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('complaints')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComplaints(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const HeroSection = () => (
    <Box
      sx={{
        bgcolor: '#000',
        color: 'white',
        py: 10,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255,227,2,0.05) 0%, rgba(0,0,0,0.9) 100%)',
          zIndex: 1
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '45%',
          height: '100%',
          opacity: 0.6,
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(2deg)',
            },
          },
          animation: 'float 6s ease-in-out infinite',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, #000 0%, transparent 100%)',
            zIndex: 2
          }
        }}
      >
        <Box
          component="img"
          src={complaintsImg}
          alt="Complaints"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            filter: 'brightness(0.8) contrast(1.2)',
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box>
              <Typography 
                component="h1" 
                sx={{
                  fontSize: { xs: '2.5rem', md: '4.5rem' },
                  fontWeight: 800,
                  mb: 2,
                  lineHeight: 1.1,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -10,
                    left: 0,
                    width: '100px',
                    height: '4px',
                    background: 'linear-gradient(90deg, #FFE302, transparent)',
                  }
                }}
              >
                <Box sx={{ display: 'block', mb: 1 }}>
                  <span style={{ color: '#FFE302' }}>Community</span>
                </Box>
                <Box sx={{ display: 'block' }}>
                  <span style={{ color: '#FFE302' }}>Support</span>{' '}
                  <span style={{ color: '#00C853' }}>Hub</span>
                </Box>
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#999',
                  mb: 4,
                  maxWidth: '600px',
                  lineHeight: 1.5,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                }}
              >
                Get help from our community experts and share your experiences with our growing network of users
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                '@keyframes slideUp': {
                  from: { transform: 'translateY(20px)', opacity: 0 },
                  to: { transform: 'translateY(0)', opacity: 1 }
                },
                animation: 'slideUp 0.8s ease-out forwards',
              }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: '#FFE302',
                    color: '#000',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 0,
                    textTransform: 'none',
                    border: '2px solid #FFE302',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#000',
                      color: '#FFE302',
                      border: '2px solid #FFE302',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 10px 20px rgba(255,227,2,0.2)'
                    }
                  }}
                >
                  Post Complaint
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    color: '#fff',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 0,
                    textTransform: 'none',
                    border: '2px solid #fff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#fff',
                      color: '#000',
                      border: '2px solid #fff',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 10px 20px rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Browse Issues
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{
                  bgcolor: 'rgba(255,227,2,0.1)',
                  p: 3,
                  border: '1px solid rgba(255,227,2,0.2)',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="h2" sx={{ color: '#FFE302', fontWeight: 800 }}>
                        1,234
                      </Typography>
                      <Typography sx={{ color: '#999' }}>
                        Total Issues
                      </Typography>
                    </Box>
                    <QuestionAnswerIcon sx={{ color: '#FFE302', fontSize: 40 }} />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{
                  bgcolor: 'rgba(0,200,83,0.1)',
                  p: 3,
                  border: '1px solid rgba(0,200,83,0.2)',
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: '#00C853', fontWeight: 800 }}>
                      89%
                    </Typography>
                    <Typography sx={{ color: '#999' }}>
                      Resolved
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{
                  bgcolor: 'rgba(33,150,243,0.1)',
                  p: 3,
                  border: '1px solid rgba(33,150,243,0.2)',
                }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: '#2196F3', fontWeight: 800 }}>
                      2h
                    </Typography>
                    <Typography sx={{ color: '#999' }}>
                      Avg Response
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  const StatCard = ({ title, value, icon, color }) => (
    <Paper sx={{
      p: 2,
      bgcolor: 'rgba(255,255,255,0.05)',
      borderRadius: 2,
      width: 150,
      textAlign: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1
      }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 40 } })}
      </Box>
      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: '#999' }}>
        {title}
      </Typography>
    </Paper>
  );

  const RecentComplaints = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        Recent Issues
      </Typography>
      <List>
        {complaints.slice(0, 5).map((complaint) => (
          <ListItem
            key={complaint.id}
            sx={{
              bgcolor: 'rgba(255,255,255,0.05)',
              mb: 1,
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              }
            }}
          >
            <ListItemIcon>
              {complaint.status === 'resolved' ? (
                <CheckCircleIcon sx={{ color: '#00C853' }} />
              ) : (
                <ErrorIcon sx={{ color: '#FFE302' }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={complaint.title}
              secondary={
                <Typography variant="caption" sx={{ color: '#999' }}>
                  {new Date(complaint.created_at).toLocaleDateString()}
                </Typography>
              }
              sx={{
                '& .MuiListItemText-primary': {
                  color: 'white',
                  fontSize: '0.9rem'
                }
              }}
            />
            <Chip
              label={complaint.status}
              size="small"
              sx={{
                bgcolor: complaint.status === 'resolved' ? 'rgba(0,200,83,0.1)' : 'rgba(255,227,2,0.1)',
                color: complaint.status === 'resolved' ? '#00C853' : '#FFE302'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const ComplaintForm = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [category, setCategory] = useState('');
    const [severity, setSeverity] = useState('medium');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setError(null);

      try {
        // Get the current user
        const { data: { user } } = await supabase.auth.getUser();
        
        // If no user is logged in and user_id is required
        if (!user) {
          throw new Error("You must be logged in to submit a complaint");
        }
        
        // Convert comma-separated tags string to array and trim whitespace
        const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);

        const { data, error: submitError } = await supabase
          .from('complaints')
          .insert([
            {
              title,
              description: content,
              tags: tagsArray,
              category,
              severity,
              status: 'pending',
              created_at: new Date().toISOString(),
              votes: 0,
              views: 0,
              user_id: user.id
            }
          ]);

        if (submitError) throw submitError;

        setSuccessMessage('Complaint posted successfully!');
        // Clear form
        setTitle('');
        setContent('');
        setTags('');
        setCategory('');
        setSeverity('medium');

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
        
        // Refresh complaints list
        fetchComplaints();

      } catch (err) {
        setError(err.message);
        console.error('Error submitting complaint:', err);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Box 
        component="form" 
        onSubmit={handleSubmit}
        sx={{
          bgcolor: '#111',
          p: 3,
          borderRadius: 2,
          border: '1px solid rgba(255,227,2,0.1)',
          '&:hover': {
            borderColor: 'rgba(255,227,2,0.2)',
          }
        }}
      >
        <Typography variant="h5" sx={{ color: '#FFE302', mb: 3, fontWeight: 600 }}>
          Post a Complaint
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover fieldset': {
                borderColor: '#FFE302',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255,255,255,0.7)',
            }
          }}
          required
        />

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Category</InputLabel>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FFE302',
              },
            }}
            required
          >
            <MenuItem value="hardware">Hardware</MenuItem>
            <MenuItem value="software">Software</MenuItem>
            <MenuItem value="network">Network</MenuItem>
            <MenuItem value="security">Security</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Severity</InputLabel>
          <Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            sx={{
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FFE302',
              },
            }}
            required
          >
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="critical">Critical</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }}>
            Description
          </Typography>
          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            theme="snow"
            style={{
              backgroundColor: '#1a1a1a',
              color: 'white',
              borderRadius: '4px',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
            sx={{
              '& .ql-toolbar': {
                border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.2)',
              },
              '& .ql-container': {
                border: 'none',
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label="Tags (comma-separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              color: 'white',
              '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover fieldset': {
                borderColor: '#FFE302',
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(255,255,255,0.7)',
            }
          }}
          helperText="Example: hardware, printer, network"
          FormHelperTextProps={{
            sx: { color: 'rgba(255,255,255,0.5)' }
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSubmitting}
          sx={{
            bgcolor: '#FFE302',
            color: '#000',
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            borderRadius: 0,
            '&:hover': {
              bgcolor: '#e6cc00',
              transform: 'translateY(-2px)',
              boxShadow: '0 5px 15px rgba(255,227,2,0.2)',
            },
            '&:disabled': {
              bgcolor: 'rgba(255,227,2,0.5)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: '#000' }} />
          ) : (
            'POST COMPLAINT'
          )}
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#000', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ bgcolor: '#111', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Box
            component="img"
            src={logo0}
            alt="Logo"
            sx={{
              height: '35px',
              mr: 2,
              filter: 'brightness(0) invert(1)'
            }}
          />
          <TextField
            placeholder="Search issues..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              ml: 2,
              width: '400px',
              '& .MuiOutlinedInput-root': {
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.1)',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.2)',
                },
                '&:hover fieldset': {
                  borderColor: '#FFE302',
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'gray' }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#FFE302' }
              }}
            >
              Home
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/services"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#FFE302' }
              }}
            >
              Services
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/solutions"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#FFE302' }
              }}
            >
              Solutions
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/ai"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#FFE302' }
              }}
            >
              AI
            </Button>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/support"
              sx={{ 
                mx: 0.5, 
                fontWeight: 600,
                '&:hover': { color: '#FFE302' }
              }}
            >
              Support
            </Button>
          </Box>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#FFE302',
              color: '#000',
              '&:hover': {
                bgcolor: '#00C853',
              }
            }}
          >
            Ask Question
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: 320,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
            bgcolor: '#1a1a1a',
            color: 'white',
            borderRight: '1px solid rgba(255,255,255,0.1)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <RecentComplaints />
          <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 2 }} />
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: '#999', mb: 1 }}>
              CATEGORIES
            </Typography>
            <List>
              {[
                { text: 'All Issues', icon: <QuestionAnswerIcon />, count: 234 },
                { text: 'Hardware', icon: <TagIcon />, count: 89 },
                { text: 'Software', icon: <TagIcon />, count: 145 },
                { text: 'Network', icon: <TagIcon />, count: 67 },
                { text: 'Other', icon: <TagIcon />, count: 32 },
              ].map((item) => (
                <ListItem
                  button
                  key={item.text}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': {
                      bgcolor: 'rgba(255,227,2,0.1)',
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: '#FFE302', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  <Chip
                    label={item.count}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: '#999'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Toolbar />
        <HeroSection />
        
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
              All Issues
            </Typography>
            <Button
              startIcon={<FilterListIcon />}
              sx={{ color: '#FFE302' }}
            >
              Filter
            </Button>
          </Box>

          {/* Questions List */}
          {complaints.map((complaint) => (
            <Card 
              key={complaint.id} 
              sx={{
                mb: 2,
                bgcolor: '#111',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                  borderColor: '#FFE302',
                }
              }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ color: '#FFE302', mb: 1 }}>
                      {complaint.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
                      {complaint.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {Array.isArray(complaint.tags) 
                        ? complaint.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(255,227,2,0.1)',
                                color: '#FFE302',
                              }}
                            />
                          ))
                        : null
                      }
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <ThumbUpIcon sx={{ color: '#00C853' }} />
                        <Typography variant="caption" display="block" sx={{ color: '#999' }}>
                          {complaint.votes || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <CommentIcon sx={{ color: '#FFE302' }} />
                        <Typography variant="caption" display="block" sx={{ color: '#999' }}>
                          {complaint.comments?.length || 0}
                        </Typography>
                      </Box>
                      <Avatar sx={{ width: 32, height: 32 }} />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {/* Rich Editor for New Question */}
          <Box py={4}>
            <Container maxWidth="lg">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
                      All Issues
                    </Typography>
                    <Button
                      startIcon={<FilterListIcon />}
                      sx={{ 
                        color: '#FFE302',
                        '&:hover': {
                          bgcolor: 'rgba(255,227,2,0.1)',
                        }
                      }}
                    >
                      FILTER
                    </Button>
                  </Box>
                  <ComplaintForm />
                </Grid>
              </Grid>
            </Container>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Complaints;